import React, { useState, useEffect, useCallback } from 'react';
import { format, differenceInDays } from 'date-fns';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import ClientSearchSelect from '../../components/ao/ClientSearchSelect';
import aoAPI from '../../api/aoAPI';

const STATUS_COLORS = {
  'Pending':    'bg-gray-50 text-gray-600 border-gray-200',
  'In Process': 'bg-blue-50 text-blue-700 border-blue-200',
  'On Hold':    'bg-amber-50 text-amber-700 border-amber-200',
  'Released':   'bg-green-50 text-green-700 border-green-200',
  'Cancelled':  'bg-red-50 text-red-600 border-red-200',
};

const EMPTY_FORM = {
  titleNumber:  '',
  titleType:    'TCT',
  client:       '',
  dateReceived: '',
  dateReleased: '',
  status:       'Pending',
  remarks:      '',
};

const fmt = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n ?? 0);

const AOLandTitleMonitor = () => {
  const [clients, setClients]           = useState([]);   // ← loaded once for ClientSearchSelect
  const [titles, setTitles]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');
  const [form, setForm]                 = useState(EMPTY_FORM);

  // Load clients once — required by ClientSearchSelect
  useEffect(() => {
    aoAPI.getClients()
      .then(r => setClients(r.data || []))
      .catch(() => {});
  }, []);

  const loadTitles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (clientFilter)           params.clientId = clientFilter;
      if (statusFilter !== 'All') params.status   = statusFilter;

      const res  = await aoAPI.getLandTitles(params);
      const data = res.data || [];

      const titlesWithExpenses = await Promise.all(
        data.map(async (title) => {
          try {
            const exp = await aoAPI.getLandTitleExpenses(title._id);
            return { ...title, runningExpenses: exp?.data ?? { totalExpenses: 0, breakdown: [] } };
          } catch {
            return { ...title, runningExpenses: { totalExpenses: 0, breakdown: [] } };
          }
        })
      );

      setTitles(titlesWithExpenses);
    } catch (err) {
      console.error('Failed to load titles:', err);
    } finally {
      setLoading(false);
    }
  }, [clientFilter, statusFilter]);

  useEffect(() => { loadTitles(); }, [loadTitles]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingTitle(null);
    setShowForm(false);
    setSaveMsg('');
  };

  const handleSave = async () => {
    if (!form.titleNumber.trim()) { setSaveMsg('Title number is required'); return; }
    if (!form.client)             { setSaveMsg('Client is required');        return; }
    if (!form.dateReceived)       { setSaveMsg('Date received is required'); return; }

    try {
      setSaving(true);
      setSaveMsg('');
      if (editingTitle) {
        await aoAPI.updateLandTitle(editingTitle._id, form);
      } else {
        await aoAPI.createLandTitle(form);
      }
      resetForm();
      await loadTitles();
    } catch (err) {
      setSaveMsg(err?.response?.data?.message || 'Failed to save land title');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (title) => {
    setForm({
      titleNumber:  title.titleNumber,
      titleType:    title.titleType,
      client:       title.client?._id || title.client,
      dateReceived: title.dateReceived?.substring(0, 10) ?? '',
      dateReleased: title.dateReleased?.substring(0, 10) ?? '',
      status:       title.status,
      remarks:      title.remarks || '',
    });
    setEditingTitle(title);
    setShowForm(true);
    setSaveMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this land title?')) return;
    try {
      await aoAPI.deleteLandTitle(id);
      await loadTitles();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete land title');
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setForm(f => ({
      ...f,
      status: newStatus,
      // Auto-set dateReleased when status flips to Released
      dateReleased: newStatus === 'Released' && !f.dateReleased
        ? new Date().toISOString().substring(0, 10)
        : f.dateReleased,
    }));
  };

  const daysInProcess = (title) => {
    const received = new Date(title.dateReceived);
    const end      = title.dateReleased ? new Date(title.dateReleased) : new Date();
    return differenceInDays(end, received);
  };

  // ─── Summary cards ──────────────────────────────────────────────────────────
  const activeTitles        = titles.filter(t => t.status === 'In Process').length;
  const onHoldTitles        = titles.filter(t => t.status === 'On Hold').length;
  const now                 = new Date();
  const releasedThisMonth   = titles.filter(t => {
    if (t.status !== 'Released' || !t.dateReleased) return false;
    const d = new Date(t.dateReleased);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const totalRunningExpenses = titles
    .filter(t => t.status === 'In Process')
    .reduce((sum, t) => sum + (t.runningExpenses?.totalExpenses ?? 0), 0);

  return (
    <Layout pageTitle="Land Title Monitor">
      <div className="p-6 max-w-6xl mx-auto space-y-6">

        {/* ── Filters + Add button ─────────────────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
              <ClientSearchSelect
                clients={clients}
                value={clientFilter}
                onChange={setClientFilter}
                placeholder="All clients"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {['All', 'Pending', 'In Process', 'On Hold', 'Released', 'Cancelled'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => { setShowForm(true); setEditingTitle(null); setForm(EMPTY_FORM); }}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <PlusIcon className="h-4 w-4" /> Add Title
              </button>
            </div>
          </div>
        </div>

        {/* ── Summary cards ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
            <p className="text-xs text-blue-600 font-medium">Active (In Process)</p>
            <p className="text-2xl font-bold text-blue-800 mt-1">{activeTitles}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-xs text-amber-600 font-medium">On Hold</p>
            <p className="text-2xl font-bold text-amber-800 mt-1">{onHoldTitles}</p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3">
            <p className="text-xs text-green-600 font-medium">Released This Month</p>
            <p className="text-2xl font-bold text-green-800 mt-1">{releasedThisMonth}</p>
          </div>
          <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
            <p className="text-xs text-teal-600 font-medium">Running Expenses</p>
            <p className="text-lg font-bold text-teal-800 mt-1">₱{fmt(totalRunningExpenses)}</p>
          </div>
        </div>

        {/* ── Entry form ───────────────────────────────────────────────────── */}
        {showForm && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {editingTitle ? '✏️ Editing Land Title' : '➕ New Land Title'}
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Title Number *</label>
                <input
                  type="text"
                  value={form.titleNumber}
                  onChange={e => setForm(f => ({ ...f, titleNumber: e.target.value }))}
                  placeholder="e.g. TCT-12345"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Title Type</label>
                <select
                  value={form.titleType}
                  onChange={e => setForm(f => ({ ...f, titleType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="TCT">TCT</option>
                  <option value="OCT">OCT</option>
                  <option value="CCT">CCT</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={handleStatusChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {['Pending', 'In Process', 'On Hold', 'Released', 'Cancelled'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Client *</label>
              <ClientSearchSelect
                clients={clients}
                value={form.client}
                onChange={val => setForm(f => ({ ...f, client: val }))}
                placeholder="Search client…"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Date Received *</label>
                <input
                  type="date"
                  value={form.dateReceived}
                  onChange={e => setForm(f => ({ ...f, dateReceived: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              {(form.status === 'Released' || form.dateReleased) && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Date Released</label>
                  <input
                    type="date"
                    value={form.dateReleased}
                    onChange={e => setForm(f => ({ ...f, dateReleased: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Remarks</label>
              <input
                type="text"
                value={form.remarks}
                onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                placeholder="Optional notes…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {saveMsg && (
              <p className="text-xs mb-3 text-red-500">{saveMsg}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-5 py-2 rounded-lg disabled:opacity-60 transition-colors"
              >
                {saving ? 'Saving…' : editingTitle ? 'Update' : 'Save Title'}
              </button>
              <button
                onClick={resetForm}
                className="border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Table ────────────────────────────────────────────────────────── */}
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-8">Loading titles…</p>
        ) : titles.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No land titles found.</p>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Title No.</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Date Received</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Running Expenses</th>
                  <th className="px-4 py-3 text-center">Days</th>
                  <th className="px-4 py-3 text-center w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {titles.map(title => (
                  <tr key={title._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{title.titleNumber}</td>
                    <td className="px-4 py-3 text-gray-500">{title.titleType}</td>
                    <td className="px-4 py-3 text-gray-700">{title.client?.name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {format(new Date(title.dateReceived), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[title.status]}`}>
                        {title.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-gray-800">
                      ₱{fmt(title.runningExpenses?.totalExpenses)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500">
                      {daysInProcess(title)}d
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleEdit(title)} className="text-gray-400 hover:text-teal-600" title="Edit">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(title._id)} className="text-gray-400 hover:text-red-500" title="Delete">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AOLandTitleMonitor;